import { z } from "zod";
import { browserAgentFindingSchema } from "./browser-agent.js";
import { ufSchema } from "./calculation.js";
import { annexEntrySchema } from "./web-research.js";
/**
 * Fiscal facts (ADR 0013, status Proposed; rollout PR 7). A fact is an
 * immutable, per-field statement (`stApplicable`, `originalMva`,
 * `destinationInternalRate`, `interstateRate`) with a match key, a validity
 * window, a source and provenance. Its status lives in a mutable projection
 * fed by an append-only event log.
 *
 * DORMANT: no calculation reads facts in this release. Facts never publish,
 * never override a published rule and are always labeled with a
 * certification level.
 */
export const FISCAL_FACT_MVA_TOLERANCE = "0.0001";
export const FISCAL_FACT_REJECT_REASON_MIN = 10;
/** The backfill job derives facts from IOB findings captured in this window. */
export const FISCAL_FACT_BACKFILL_DAYS = 180;
/** The maintenance job flags CERTIFIED facts whose validTo is this close. */
export const FISCAL_FACT_REVALIDATION_LEAD_DAYS = 30;
const ncmSchema = z.string().regex(/^\d{8}$/);
const ncmPrefixSchema = z.string().regex(/^\d{2,8}$/);
const cestSchema = z.string().regex(/^\d{2}\.\d{3}\.\d{2}$/);
const rateSchema = z.string().regex(/^(0|[1-9]\d{0,5})(\.\d{1,8})?$/);
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
export const fiscalFactKindSchema = z.enum([
    /** NCM + origin → destination route (IOB, VERIFY_PAIR). */
    "ST_ROUTE",
    /** CEST segment × destination UF with an origin scope (annex entry). */
    "ST_SEGMENT",
    /** No CEST lists the NCM (catalog derivation). */
    "NO_ST_CATALOG",
    /** UF internal rate: modal, or UF × NCM/CEST when the annex states it. */
    "INTERNAL_RATE",
    /** Interstate rate for origin × destination. */
    "INTERSTATE_RATE",
]);
export const fiscalFactSourceSchema = z.enum([
    "IOB_SIMULADOR",
    "WEB_RESEARCH",
    "OPERATOR",
    "CATALOG_DERIVATION",
]);
export const fiscalFactStatusSchema = z.enum([
    "CERTIFIED",
    "UNVERIFIED",
    "VERIFIED",
    "CONFLICTING",
    "REJECTED",
    "SUPERSEDED",
    "EXPIRED",
]);
/** Rank, strongest first: PUBLISHED_RULE > CERTIFIED = VERIFIED > UNVERIFIED > NONE. */
export const certificationLevelSchema = z.enum([
    "PUBLISHED_RULE",
    "CERTIFIED",
    "VERIFIED",
    "UNVERIFIED",
    "NONE",
]);
export const fiscalFactFieldNameSchema = z.enum([
    "stApplicable",
    "originalMva",
    "destinationInternalRate",
    "interstateRate",
]);
/**
 * Rates are decimal fractions (0.18 = 18 %). Absent = the fact is silent on it.
 *
 * `internalStOnly` / `internalStUf` annotate an IOB `INTERNAL_ST_ONLY` answer
 * on an interstate route (ADR 0013 notes): no ST retained by the sender on the
 * route (`stApplicable: false`), while the destination UF applies ST on its
 * own internal operations (antecipação / ST interna no destino). They are
 * informational: resolution and corroboration read `stApplicable` only.
 */
export const fiscalFactFieldsSchema = z
    .object({
    stApplicable: z.boolean().optional(),
    originalMva: rateSchema.optional(),
    destinationInternalRate: rateSchema.optional(),
    interstateRate: rateSchema.optional(),
    internalStOnly: z.literal(true).optional(),
    internalStUf: ufSchema.optional(),
})
    .strict()
    .refine((fields) => fields.internalStOnly !== true || fields.stApplicable === false, {
    message: "internalStOnly requires stApplicable false",
    path: ["internalStOnly"],
});
export const fiscalFactOriginScopeSchema = z.enum(["ALL_ORIGINS", "ORIGINS", "INTERNAL_ONLY"]);
/**
 * What a confirmation covers (CONFAZ origin scope, ADR 0013 notes).
 * - AS_EXTRACTED: every origin the fact's own match states (the behaviour
 *   before decision scopes existed; the default when a client omits it).
 * - INTERNAL_ONLY: only the destination's internal operations (origin =
 *   destination). A destination's ST annex always covers them; interstate
 *   retention depends on the CONFAZ agreement the origin signed, so interstate
 *   routes keep the level UNVERIFIED (left to the IOB and the agreement
 *   matrix).
 */
export const fiscalFactDecisionScopeSchema = z.enum([
    "AS_EXTRACTED",
    "INTERNAL_ONLY",
    /**
     * Internal operations plus the interstate origins the CONFAZ agreement
     * matrix binds with the destination on each operation date (adhesions,
     * denúncias and exclusions apply as they happen). Offered when the matrix
     * knows the segment × destination.
     */
    "AGREEMENT_ORIGINS",
]);
/** What a verification covered before a scope correction narrowed it. */
export const fiscalFactPreviousScopeSchema = z
    .object({
    /** AS_EXTRACTED: the whole match (verifiedScope was null). */
    mode: z.enum(["AS_EXTRACTED", "INTERNAL_ONLY", "AGREEMENT_ORIGINS", "ORIGINS"]),
    originUfs: z.array(ufSchema).max(27),
})
    .strict();
/**
 * Set on a verified scope that an audited correction ("Restringir escopo de
 * origem") narrowed after the confirmation. A restricted scope is a hard
 * cap: origins outside `originUfs` answer at the level UNVERIFIED even when
 * the CONFAZ matrix knows the route. Widening again goes through a normal
 * re-review, never through a correction.
 */
export const fiscalFactScopeRestrictionMarkSchema = z
    .object({
    correctionId: z.string().uuid(),
    reason: z.string().min(1).max(1_000),
    restrictedAt: z.string().datetime(),
    restrictedBy: z.string().uuid(),
    previous: fiscalFactPreviousScopeSchema,
})
    .strict();
/**
 * Stored on a VERIFIED fact whose confirmation narrowed its origins. Absent
 * or null: the verification covers the fact's whole match.
 */
export const fiscalFactVerifiedScopeSchema = z.discriminatedUnion("mode", [
    z
        .object({
        mode: z.literal("INTERNAL_ONLY"),
        /** The only origin the verification covers (= the fact's destination). */
        originUfs: z.array(ufSchema).min(1).max(27),
        decidedAt: z.string().datetime(),
        reviewSessionId: z.string().uuid().nullable(),
        /** Present when a scope correction narrowed the verification to this. */
        restriction: fiscalFactScopeRestrictionMarkSchema.optional(),
    })
        .strict(),
    z
        .object({
        /**
         * A scope correction narrowed the verification to exactly these
         * origins (a subset of what the fact states). Origins outside the list
         * answer at the level UNVERIFIED.
         */
        mode: z.literal("ORIGINS"),
        originUfs: z.array(ufSchema).min(1).max(27),
        decidedAt: z.string().datetime(),
        reviewSessionId: z.string().uuid().nullable(),
        restriction: fiscalFactScopeRestrictionMarkSchema,
    })
        .strict(),
    z
        .object({
        mode: z.literal("AGREEMENT_ORIGINS"),
        /**
         * Snapshot for the audit: destination + signatory origins on `asOf`.
         * Resolution re-derives the origins from the matrix on each date.
         */
        originUfs: z.array(ufSchema).min(1).max(27),
        agreementKeys: z.array(z.string().min(1).max(40)).max(20),
        asOf: z.string().date(),
        decidedAt: z.string().datetime(),
        reviewSessionId: z.string().uuid().nullable(),
    })
        .strict(),
]);
/**
 * Match key. `ST_ROUTE`: ncm, originUf, destinationUf (cest optional).
 * `ST_SEGMENT`: destinationUf, originScope (+ originUfs), cest and/or
 * ncmPrefixes. UF-level kinds carry only the UFs they state.
 */
export const fiscalFactMatchSchema = z
    .object({
    ncm: ncmSchema.optional(),
    ncmPrefixes: z.array(ncmPrefixSchema).max(50).optional(),
    cest: cestSchema.nullable().optional(),
    originUf: ufSchema.optional(),
    originScope: fiscalFactOriginScopeSchema.optional(),
    originUfs: z.array(ufSchema).max(27).optional(),
    destinationUf: ufSchema.optional(),
})
    .strict();
export const fiscalFactCitationSchema = z
    .object({
    url: z.string().url().max(2_000),
    host: z.string().min(1).max(253),
    sourceKey: z.string().min(1).max(120).nullable(),
    evidenceId: z.string().uuid().nullable(),
    quote: z.string().max(4_000),
})
    .strict();
export const fiscalFactSchema = z
    .object({
    id: z.string().uuid(),
    kind: fiscalFactKindSchema,
    source: fiscalFactSourceSchema,
    status: fiscalFactStatusSchema,
    /** CERTIFIED/VERIFIED → that level; UNVERIFIED → UNVERIFIED; otherwise NONE. */
    level: certificationLevelSchema,
    /** Flag, not a status: the fact stays usable and shows "Revalidação pendente". */
    revalidationRequested: z.boolean(),
    match: fiscalFactMatchSchema,
    fields: fiscalFactFieldsSchema,
    validFrom: z.string().date(),
    validTo: z.string().date().nullable(),
    /** False when the source stated no start: validFrom is the capture date. */
    validFromStated: z.boolean(),
    /** min(validTo, supersession close); null = open ended. */
    effectiveValidTo: z.string().date().nullable(),
    /** When the source observed the values (finding capture, annex extraction). */
    checkedAt: z.string().datetime(),
    /** Latest re-check (an identical later observation), else checkedAt. */
    lastCheckedAt: z.string().datetime(),
    findingId: z.string().uuid().nullable(),
    annexEntryId: z.string().uuid().nullable(),
    evidenceIds: z.array(z.string().uuid()).max(20),
    citations: z.array(fiscalFactCitationSchema).max(20),
    legalBasis: z.string().max(1_000).nullable(),
    corroboratedByFactId: z.string().uuid().nullable(),
    supersededByFactId: z.string().uuid().nullable(),
    verifiedBy: z.string().uuid().nullable(),
    verifiedAt: z.string().datetime().nullable(),
    /**
     * Set when the confirmation narrowed the origins (decision scope
     * INTERNAL_ONLY); null/absent = the whole match is verified.
     */
    verifiedScope: fiscalFactVerifiedScopeSchema.nullable().optional(),
    contentDigest: sha256Schema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    /** State revision; the ETag for verification If-Match. */
    revision: z.number().int().positive(),
})
    .strict();
export const fiscalFactEventActionSchema = z.enum([
    "DERIVED",
    "RECHECKED",
    "CORROBORATED",
    "CONFLICT_DETECTED",
    "SUPERSEDED",
    "REVALIDATION_REQUESTED",
    "CONFIRMED",
    "REJECTED",
    "IOB_REQUESTED",
    "REVOKED",
    "EXPIRED",
    /** An audited correction narrowed the origins a VERIFIED fact covers (new revision). */
    "SCOPE_RESTRICTED",
]);
export const fiscalFactEventSchema = z
    .object({
    id: z.string().uuid(),
    factId: z.string().uuid(),
    action: fiscalFactEventActionSchema,
    /** Status and flag after the event. */
    status: fiscalFactStatusSchema,
    revalidationRequested: z.boolean(),
    reasonCode: z.string().min(1).max(80).nullable(),
    actorId: z.string().uuid().nullable(),
    machineSubject: z.string().min(1).max(200).nullable(),
    /** The other fact involved (corroborating, conflicting or superseding). */
    relatedFactId: z.string().uuid().nullable(),
    findingId: z.string().uuid().nullable(),
    note: z.string().max(1_000).nullable(),
    evidenceIds: z.array(z.string().uuid()).max(20),
    /**
     * Set when a group review confirmed the fact (reasonCode
     * GROUP_REVIEW_INSPECTED: inspected directly; GROUP_REVIEW_SAMPLED:
     * included by the session's sample).
     */
    reviewSessionId: z.string().uuid().nullable().optional(),
    /** CONFIRMED with a narrowed decision scope, or SCOPE_RESTRICTED: the scope it stored. */
    verifiedScope: fiscalFactVerifiedScopeSchema.nullable().optional(),
    at: z.string().datetime(),
})
    .strict();
export const fiscalFactPageSchema = z
    .object({
    items: z.array(fiscalFactSchema).max(200),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
    totalEstimate: z.number().int().nonnegative(),
})
    .strict();
/** Annex row a ST_SEGMENT fact was derived from (admin read model). */
export const fiscalFactAnnexEntrySchema = annexEntrySchema
    .extend({
    id: z.string().uuid(),
    unitId: z.string().uuid(),
    evidenceId: z.string().uuid(),
    contentSha256: sha256Schema,
    destinationUf: ufSchema.nullable(),
    segment: z
        .string()
        .regex(/^\d{2}$/)
        .nullable(),
    extractedAt: z.string().datetime(),
})
    .strict();
/**
 * Where a fact's pending revalidation stands in the IOB agent queue. The
 * runtime queues one IOB lookup per revalidation request when the agent polls
 * (and at once when an operator requests the IOB check):
 * - AWAITING_AGENT: flagged, queued at the agent's next poll (`nextWindowAt`:
 *   the next active block of its weekly schedule, now when inside one);
 * - QUEUED / IN_PROGRESS: in the queue (`lookupsAhead`, `estimatedAt` at the
 *   agent's pace within its schedule);
 * - CHECKED: the IOB answered after the request (the flag clears when it
 *   repeats the fact; otherwise the new IOB fact supersedes or conflicts);
 * - FAILED / SKIPPED: the lookup ended without an answer (request IOB again);
 * - NOT_QUEUEABLE: the IOB checks NCM × route only (ST_ROUTE facts).
 */
export const fiscalFactRevalidationScheduleSchema = z
    .object({
    state: z.enum([
        "AWAITING_AGENT",
        "QUEUED",
        "IN_PROGRESS",
        "CHECKED",
        "FAILED",
        "SKIPPED",
        "NOT_QUEUEABLE",
    ]),
    requestedAt: z.string().datetime().nullable(),
    taskId: z.string().uuid().nullable(),
    taskItemId: z.string().uuid().nullable(),
    lookupsAhead: z.number().int().nonnegative().nullable(),
    estimatedAt: z.string().datetime().nullable(),
    nextWindowAt: z.string().datetime().nullable(),
    /** The agent's kill switch is on: nothing runs until an admin resumes it. */
    agentPaused: z.boolean(),
    /** Last change of the queue item. */
    updatedAt: z.string().datetime().nullable(),
})
    .strict();
export const fiscalFactDetailSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    fact: fiscalFactSchema,
    /** Oldest first. */
    events: z.array(fiscalFactEventSchema).max(500),
    finding: browserAgentFindingSchema.nullable(),
    annexEntry: fiscalFactAnnexEntrySchema.nullable(),
    citations: z.array(fiscalFactCitationSchema).max(20),
    /** Set when revalidation is requested; null otherwise. */
    revalidation: fiscalFactRevalidationScheduleSchema.nullable().optional(),
})
    .strict();
/* -- Verification (REVIEW:TAX_RULE:ALL, If-Match) ---------------------------- */
export const FISCAL_FACT_SCOPE_JUSTIFICATION_MIN = 20;
/**
 * The explicit acknowledgement a confirmation needs when the reviewer keeps
 * every extracted origin (AS_EXTRACTED) although the proposal is narrower
 * (internal operations or the CONFAZ signatories): "O documento prova
 * retenção de todas as UFs", plus why.
 */
export const fiscalFactScopeAcknowledgementSchema = z
    .object({
    allOriginsProven: z.literal(true),
    justification: z.string().trim().min(FISCAL_FACT_SCOPE_JUSTIFICATION_MIN).max(1_000),
})
    .strict();
/* -- Scope correction (REVIEW:TAX_RULE:ALL) ---------------------------------- */
/** The narrower origin scope a correction applies. */
export const fiscalFactScopeRestrictionTargetSchema = z.discriminatedUnion("mode", [
    /** Only the destination's internal operations (UF→UF). */
    z
        .object({ mode: z.literal("INTERNAL_ONLY") })
        .strict(),
    /** Exactly these origins (a subset of what each fact already covers). */
    z
        .object({ mode: z.literal("ORIGINS"), originUfs: z.array(ufSchema).min(1).max(27) })
        .strict(),
]);
/**
 * "Restringir escopo de origem": narrows the origins VERIFIED facts cover,
 * one fact (If-Match: its revision) or every fact a confirmed review session
 * verified. It appends a SCOPE_RESTRICTED event (a new fact revision) and
 * never deletes history. Idempotent: the same request replays its result.
 */
export const fiscalFactScopeRestrictionSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    restriction: fiscalFactScopeRestrictionTargetSchema,
    reason: z.string().trim().min(FISCAL_FACT_REJECT_REASON_MIN).max(1_000),
})
    .strict();
export const fiscalFactScopeRestrictionSkipReasonSchema = z.enum([
    /** Not VERIFIED any more (rejected, superseded…): nothing to narrow. */
    "NOT_VERIFIED",
    "NOT_FOUND",
    /** Only ST_SEGMENT facts with a destination carry an origin scope. */
    "NOT_ST_SEGMENT",
    /** The fact already covers exactly this scope (or less). */
    "ALREADY_WITHIN",
    /** The target names origins the fact does not state. */
    "OUTSIDE_EXTRACTED",
    /** The target adds origins the verification does not cover today (re-review instead). */
    "WIDENS",
]);
export const fiscalFactScopeCorrectionResultSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    correctionId: z.string().uuid(),
    target: z
        .object({
        type: z.enum(["FACT", "REVIEW_SESSION"]),
        id: z.string().uuid(),
    })
        .strict(),
    restriction: fiscalFactScopeRestrictionTargetSchema,
    reason: z.string().min(1).max(1_000),
    restricted: z
        .array(z
        .object({
        factId: z.string().uuid(),
        revision: z.number().int().positive(),
        previous: fiscalFactPreviousScopeSchema,
        originUfs: z.array(ufSchema).min(1).max(27),
    })
        .strict())
        .max(500),
    skipped: z
        .array(z
        .object({
        factId: z.string().uuid(),
        reason: fiscalFactScopeRestrictionSkipReasonSchema,
        status: fiscalFactStatusSchema.nullable(),
    })
        .strict())
        .max(500),
    counts: z
        .object({
        facts: z.number().int().nonnegative(),
        restricted: z.number().int().nonnegative(),
        skipped: z.number().int().nonnegative(),
    })
        .strict(),
    actorId: z.string().uuid(),
    correlationId: z.string().min(1).max(200),
    restrictedAt: z.string().datetime(),
    /** True when this response replays an earlier identical correction. */
    replayed: z.boolean(),
})
    .strict();
export const fiscalFactVerificationSchema = z.discriminatedUnion("action", [
    z
        .object({
        schemaVersion: z.literal("1.0.0"),
        action: z.literal("CONFIRM"),
        evidenceIds: z.array(z.string().uuid()).max(20).optional(),
        /** Omitted = AS_EXTRACTED. INTERNAL_ONLY needs a ST_SEGMENT fact that covers internal operations. */
        decisionScope: fiscalFactDecisionScopeSchema.optional(),
        /**
         * Required when AS_EXTRACTED covers more origins than the proposal
         * (all origins while the CONFAZ signatories are not verified).
         */
        scopeAcknowledgement: fiscalFactScopeAcknowledgementSchema.optional(),
        note: z.string().trim().max(1_000).optional(),
    })
        .strict(),
    z
        .object({
        schemaVersion: z.literal("1.0.0"),
        action: z.literal("REJECT"),
        reason: z.string().trim().min(FISCAL_FACT_REJECT_REASON_MIN).max(1_000),
    })
        .strict(),
    z
        .object({
        schemaVersion: z.literal("1.0.0"),
        action: z.literal("REQUEST_IOB"),
        note: z.string().trim().max(1_000).optional(),
    })
        .strict(),
]);
/* -- Bulk revoke (ADMIN:TAX_PLATFORM:ALL) ------------------------------------ */
export const fiscalFactRevokeSelectorSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("SWEEP"), sweepId: z.string().uuid() }).strict(),
    z
        .object({
        kind: z.literal("HOST"),
        host: z
            .string()
            .min(1)
            .max(253)
            .regex(/^[a-z0-9.-]+$/),
    })
        .strict(),
    z.object({ kind: z.literal("SOURCE"), source: fiscalFactSourceSchema }).strict(),
]);
export const fiscalFactRevokeSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    selector: fiscalFactRevokeSelectorSchema,
    reason: z.string().trim().min(FISCAL_FACT_REJECT_REASON_MIN).max(1_000),
})
    .strict();
export const fiscalFactRevokeResultSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    revoked: z.number().int().nonnegative(),
    /** First 500 revoked ids. */
    factIds: z.array(z.string().uuid()).max(500),
})
    .strict();
/* -- Verification queue ----------------------------------------------------- */
export const fiscalFactQueueTabSchema = z.enum([
    "unverified",
    "conflicting",
    "drift",
    "revalidation",
    "expired",
]);
export const fiscalFactDriftSchema = z
    .object({
    rulesetVersion: z.string().min(1),
    fiscalRuleId: z.string().min(1),
    fields: z.array(fiscalFactFieldNameSchema).min(1).max(4),
    rule: fiscalFactFieldsSchema,
    /**
     * ICMS treatment of the selected rule. `EXCLUDED` means the rule is silent
     * on ICMS (a national TIPI row): under `legacy-v1`/`derived-mva-v1` the
     * engine still selects it and reports ICMS 0, which is not an exemption.
     */
    ruleIcmsTreatment: z.enum(["TAXED", "EXEMPT", "EXCLUDED"]).optional(),
})
    .strict();
export const fiscalFactQueueItemSchema = z
    .object({
    fact: fiscalFactSchema,
    /** Open coverage gaps this fact would answer (sum of occurrence counts). */
    impact: z
        .object({
        gapCount: z.number().int().nonnegative(),
        occurrenceCount: z.number().int().nonnegative(),
    })
        .strict(),
    /** Facts on the other side of a conflict (active, same key). */
    conflictingFactIds: z.array(z.string().uuid()).max(50),
    /** `drift` tab only: the published rule that disagrees. */
    drift: fiscalFactDriftSchema.nullable(),
    /** Set when revalidation is requested; null otherwise. */
    revalidation: fiscalFactRevalidationScheduleSchema.nullable().optional(),
})
    .strict();
export const fiscalFactQueuePageSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    tab: fiscalFactQueueTabSchema,
    items: z.array(fiscalFactQueueItemSchema).max(200),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
    totalEstimate: z.number().int().nonnegative(),
})
    .strict();
/* -- Resolution (pure, packages/domain/src/facts.ts) ------------------------- */
export const fiscalFactsPolicySchema = z.enum(["OFF", "CERTIFIED_ONLY", "ALL"]);
export const fiscalFactFieldResolutionSchema = z
    .object({
    status: z.enum(["RESOLVED", "CONFLICT", "MISSING", "NOT_NEEDED"]),
    /** Boolean for stApplicable, decimal string for rates; null unless RESOLVED. */
    value: z.union([z.boolean(), rateSchema]).nullable(),
    level: certificationLevelSchema,
    factId: z.string().uuid().nullable(),
    source: fiscalFactSourceSchema.nullable(),
    kind: fiscalFactKindSchema.nullable(),
    /** Facts at the winning specificity and level (the conflict set on CONFLICT). */
    candidateFactIds: z.array(z.string().uuid()).max(50),
})
    .strict();
export const fiscalFactResolutionSchema = z
    .object({
    /**
     * RESOLVED: every needed field resolved. INCOMPLETE: some needed field is
     * missing (never computed; the engine's "0" default is never reached).
     * CONFLICT: a needed field conflicts. AMBIGUOUS: several CESTs match and
     * the query has none. NONE: no usable fact.
     */
    status: z.enum(["RESOLVED", "INCOMPLETE", "CONFLICT", "AMBIGUOUS", "NONE"]),
    /** Weakest level among the fields actually used; NONE when nothing resolved. */
    level: certificationLevelSchema,
    complete: z.boolean(),
    revalidationRequested: z.boolean(),
    cestMatch: z.enum(["EXACT", "NCM_UNIQUE", "AMBIGUOUS", "NONE"]),
    fields: z
        .object({
        stApplicable: fiscalFactFieldResolutionSchema,
        originalMva: fiscalFactFieldResolutionSchema,
        destinationInternalRate: fiscalFactFieldResolutionSchema,
        interstateRate: fiscalFactFieldResolutionSchema,
    })
        .strict(),
    missingFields: z.array(fiscalFactFieldNameSchema).max(4),
    conflictFields: z.array(fiscalFactFieldNameSchema).max(4),
    /** Facts used by resolved fields, sorted. */
    factIds: z.array(z.string().uuid()).max(4),
    /**
     * How the CONFAZ matrix shaped the ST answer on this route (ST_SEGMENT
     * facts on interstate routes). AGREEMENT_SIGNATORY: origin and destination
     * bound on the date; AGREEMENT_NOT_SIGNATORY: no retention by the sender
     * (the destination may charge the recipient: antecipação);
     * INTERNAL_ONLY_DECISION: the confirmation covered internal operations
     * only and the matrix does not know the route (level UNVERIFIED);
     * RESTRICTED_SCOPE: a scope correction excluded this origin (level
     * UNVERIFIED, whatever the matrix says).
     */
    routeScope: z
        .object({
        basis: z.enum([
            "AGREEMENT_SIGNATORY",
            "AGREEMENT_NOT_SIGNATORY",
            "INTERNAL_ONLY_DECISION",
            "RESTRICTED_SCOPE",
        ]),
        agreementKeys: z.array(z.string().min(1).max(40)).max(20),
    })
        .strict()
        .nullable()
        .optional(),
})
    .strict();
export const fiscalFactResolveQuerySchema = z
    .object({
    ncm: ncmSchema,
    cest: cestSchema.optional(),
    originUf: ufSchema,
    destinationUf: ufSchema,
    date: z.string().date(),
    policy: z.enum(["CERTIFIED_ONLY", "ALL"]).default("ALL"),
})
    .strict();
export const fiscalFactResolveResponseSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    query: z
        .object({
        ncm: ncmSchema,
        cest: cestSchema.nullable(),
        originUf: ufSchema,
        destinationUf: ufSchema,
        date: z.string().date(),
        policy: z.enum(["CERTIFIED_ONLY", "ALL"]),
    })
        .strict(),
    /** The active published rule for ICMS, when one covers the key (it always wins). */
    publishedRule: z
        .object({
        rulesetVersion: z.string().min(1),
        policyVersion: z.string().min(1),
        mode: z.enum(["EXACT", "NCM_UNIQUE", "AMBIGUOUS", "MISSING"]),
        fiscalRuleId: z.string().min(1).nullable(),
    })
        .strict()
        .nullable(),
    /** True when a calculation could consult facts (rule MISSING under tax-family-v1). */
    factsConsultable: z.boolean(),
    resolution: fiscalFactResolutionSchema,
})
    .strict();
/* -- Settings ----------------------------------------------------------------- */
export const fiscalFactsModeSchema = z.enum(["OFF", "SHADOW", "ON"]);
export const fiscalFactsSettingsUpdateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    mode: fiscalFactsModeSchema,
    useCertified: z.boolean(),
    /** Kill switch for web data. */
    useUnverified: z.boolean(),
})
    .strict();
export const fiscalFactsSettingsSchema = fiscalFactsSettingsUpdateSchema
    .extend({
    /** Deploy-time API env FISCAL_FACTS_MODE (read only). */
    environmentMode: fiscalFactsModeSchema,
    /** The more restrictive of `mode` and `environmentMode`. */
    effectiveMode: fiscalFactsModeSchema,
    updatedAt: z.string().datetime().nullable(),
    updatedBy: z.string().uuid().nullable(),
    revision: z.number().int().nonnegative(),
})
    .strict();
export const FISCAL_FACTS_DEFAULT_SETTINGS = {
    schemaVersion: "1.0.0",
    mode: "OFF",
    useCertified: true,
    useUnverified: false,
};
const MODE_RANK = { OFF: 0, SHADOW: 1, ON: 2 };
/** The more restrictive of two modes (OFF < SHADOW < ON). */
export function restrictiveFiscalFactsMode(left, right) {
    return MODE_RANK[left] <= MODE_RANK[right] ? left : right;
}
/* -- Shadow comparison (FISCAL_FACTS_MODE=SHADOW) ------------------------------ */
/*
 * SHADOW computes each item a second time with VERIFIED/CERTIFIED facts in
 * place of the published ICMS parameters and stores the difference in the
 * calculation audit. Amounts returned to callers stay rule-based.
 */
const shadowMoneySchema = z.string().regex(/^(0|[1-9]\d{0,15})(\.\d{1,2})?$/);
const shadowSignedMoneySchema = z.string().regex(/^-?(0|[1-9]\d{0,15})(\.\d{1,2})?$/);
const shadowRateSchema = z.string().regex(/^-?(0|[1-9]\d{0,5})(\.\d{1,8})?$/);
export const factsShadowItemStatusSchema = z.enum([
    /** Facts answer every field needed: a fact-based result exists. */
    "COMPARED",
    "NO_FACTS",
    "INCOMPLETE",
    "CONFLICT",
    "AMBIGUOUS",
    /** NCM or a UF missing on the item: nothing to look up. */
    "NOT_COMPARABLE",
]);
export const factsShadowResultSchema = z
    .object({
    status: z.enum(["CALCULATED", "REVIEW_REQUIRED"]),
    icmsSt: shadowMoneySchema,
    taxTotal: shadowMoneySchema,
    landedUnitCost: shadowMoneySchema,
    destinationInternalIcmsRate: shadowRateSchema,
    interstateIcmsRate: shadowRateSchema,
    originalMva: shadowRateSchema,
    adjustedMva: shadowRateSchema,
})
    .strict();
export const factsShadowFieldSchema = z
    .object({
    field: fiscalFactFieldNameSchema,
    /** Value the rule-based calculation used; null when no rule stated it. */
    rule: z.union([z.boolean(), shadowRateSchema]).nullable(),
    /** Resolved fact value; null when facts do not state or need it. */
    fact: z.union([z.boolean(), shadowRateSchema]).nullable(),
    agrees: z.boolean(),
    level: certificationLevelSchema,
    factId: z.string().uuid().nullable(),
})
    .strict();
export const factsShadowItemSchema = z
    .object({
    itemId: z.string().min(1),
    position: z.number().int().nonnegative(),
    status: factsShadowItemStatusSchema,
    /** Published rule lookup of the returned result (MISSING = facts would fill a gap). */
    ruleLookup: z.enum(["EXACT", "NCM_UNIQUE", "MISSING", "AMBIGUOUS"]),
    level: certificationLevelSchema,
    factIds: z.array(z.string().uuid()).max(50),
    revalidationRequested: z.boolean(),
    fields: z.array(factsShadowFieldSchema).max(4),
    ruleBased: factsShadowResultSchema,
    factBased: factsShadowResultSchema.nullable(),
    delta: z
        .object({
        icmsSt: shadowSignedMoneySchema,
        taxTotal: shadowSignedMoneySchema,
        landedUnitCost: shadowSignedMoneySchema,
    })
        .strict()
        .nullable(),
    /** A fact-based result exists and differs in status or any amount. */
    changed: z.boolean(),
    /**
     * Present when the resolved facts say "no ST": which source certified it
     * and whether the destination applies ST on its internal operations only
     * (IOB INTERNAL_ST_ONLY). Absent on records stored before it existed.
     */
    stNote: z
        .object({
        source: fiscalFactSourceSchema,
        internalStOnly: z.boolean(),
        internalStUf: ufSchema.nullable(),
        /** No ST because the origin is not a signatory of the segment's agreement(s). */
        agreementKeys: z.array(z.string().min(1).max(40)).max(20).optional(),
    })
        .strict()
        .optional(),
})
    .strict();
export const factsShadowRecordSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    mode: z.literal("SHADOW"),
    policy: z.literal("CERTIFIED_ONLY"),
    /** UNAVAILABLE: facts could not be loaded; the calculation itself was unaffected. */
    status: z.enum(["COMPLETED", "UNAVAILABLE"]),
    asOf: z.string().date(),
    factsDigest: z
        .string()
        .regex(/^sha256:[a-f0-9]{64}$/)
        .nullable(),
    factCount: z.number().int().nonnegative(),
    summary: z
        .object({
        items: z.number().int().nonnegative(),
        compared: z.number().int().nonnegative(),
        changed: z.number().int().nonnegative(),
        noFacts: z.number().int().nonnegative(),
        incomplete: z.number().int().nonnegative(),
        conflict: z.number().int().nonnegative(),
    })
        .strict(),
    items: z.array(factsShadowItemSchema).max(500),
})
    .strict();
/** Admin aggregate over stored shadow comparisons in a recent window. */
export const factsShadowSummarySchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    environmentMode: fiscalFactsModeSchema,
    effectiveMode: fiscalFactsModeSchema,
    windowDays: z.number().int().positive(),
    from: z.string().datetime(),
    calculations: z.number().int().nonnegative(),
    items: z.number().int().nonnegative(),
    compared: z.number().int().nonnegative(),
    changed: z.number().int().nonnegative(),
    /** Compared items whose published lookup was MISSING. */
    gapsFilled: z.number().int().nonnegative(),
    /** Changed items that had a published rule. */
    ruleDisagreements: z.number().int().nonnegative(),
    noFacts: z.number().int().nonnegative(),
    incomplete: z.number().int().nonnegative(),
    conflict: z.number().int().nonnegative(),
    byLevel: z
        .object({
        CERTIFIED: z.number().int().nonnegative(),
        VERIFIED: z.number().int().nonnegative(),
    })
        .strict(),
    landedDelta: z.object({ total: shadowSignedMoneySchema, maxAbs: shadowMoneySchema }).strict(),
    topRoutes: z
        .array(z
        .object({
        ncm: ncmSchema,
        originUf: ufSchema,
        destinationUf: ufSchema,
        compared: z.number().int().nonnegative(),
        changed: z.number().int().nonnegative(),
        landedDeltaTotal: shadowSignedMoneySchema,
    })
        .strict())
        .max(10),
    lastComparedAt: z.string().datetime().nullable(),
})
    .strict();
/* -- Jobs --------------------------------------------------------------------- */
export const fiscalFactsBackfillResultSchema = z
    .object({
    scanned: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    rechecked: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
})
    .strict();
export const fiscalFactsMaintenanceResultSchema = z
    .object({
    today: z.string().date(),
    expired: z.number().int().nonnegative(),
    revalidationFlagged: z.number().int().nonnegative(),
})
    .strict();
//# sourceMappingURL=fiscal-facts.js.map